import type { GamificationMeResponse } from '@crowdfpl/domain/gamification';
import { Calendar, CheckCircle2, Crown, type LucideIcon, MessageSquare } from 'lucide-react';
import { type ReactElement } from 'react';
import { cn } from '@/helpers/utils';

type ProgressBreakdown = GamificationMeResponse['progress']['breakdown'];

type BreakdownKey = keyof ProgressBreakdown;

type BreakdownCategory = {
  key: BreakdownKey;
  label: string;
  icon: LucideIcon;
  iconClassName: string;
  chipClassName: string;
};

const BREAKDOWN_CATEGORIES: readonly BreakdownCategory[] = [
  {
    key: 'duel_votes',
    label: 'Duel votes',
    icon: MessageSquare,
    iconClassName: 'text-primary',
    chipClassName: 'bg-primary/10 text-primary',
  },
  {
    key: 'correct_calls',
    label: 'Correct calls',
    icon: CheckCircle2,
    iconClassName: 'text-primary',
    chipClassName: 'bg-primary/10 text-primary',
  },
  {
    key: 'captain_votes',
    label: 'Captain votes',
    icon: Crown,
    iconClassName: 'text-gold',
    chipClassName: 'bg-gold/10 text-gold',
  },
  {
    key: 'active_gameweeks',
    label: 'Active GWs',
    icon: Calendar,
    iconClassName: 'text-competitive-blue',
    chipClassName: 'bg-competitive-blue/10 text-competitive-blue',
  },
] as const;

type XpBreakdownGridProps = {
  breakdown: ProgressBreakdown;
  className?: string;
};

/**
 * All-time XP breakdown grid. Values are API-provided; formatting is locale-aware.
 * Layout: 2×2 on mobile, 4 columns from `sm` up.
 */
export function XpBreakdownGrid({ breakdown, className }: XpBreakdownGridProps): ReactElement {
  return (
    <div className={cn(className)} data-testid="xp-breakdown-grid">
      <h3 className="mb-2 text-[13px] font-bold text-foreground sm:text-sm">Your XP breakdown</h3>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {BREAKDOWN_CATEGORIES.map(category => {
          const line = breakdown[category.key];
          const Icon = category.icon;
          return (
            <div
              key={category.key}
              data-testid={`xp-breakdown-${category.key}`}
              className="rounded-lg border border-border/60 bg-card p-2.5 sm:p-3"
            >
              <div className="flex items-center gap-1.5">
                <Icon className={cn('h-4 w-4 shrink-0', category.iconClassName)} strokeWidth={2.2} aria-hidden />
                <span className="truncate text-[11px] text-muted-foreground">{category.label}</span>
              </div>
              <div className="mt-1.5 flex items-baseline justify-between gap-2">
                <span className="text-lg font-black leading-none tabular-nums text-foreground sm:text-xl">
                  {line.count.toLocaleString()}
                </span>
                <span
                  className={cn(
                    'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums',
                    category.chipClassName,
                  )}
                >
                  +{line.rate.toLocaleString()} XP each
                </span>
              </div>
              <div className="mt-2 border-t border-border/50 pt-2 text-[11px] tabular-nums text-muted-foreground">
                = <span className="font-bold text-foreground">{line.xp.toLocaleString()}</span> XP
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
